import { throttle } from '@goodboydigital/astro';
import p2 from 'p2';
import { Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { CullingComponent } from '../components/CullingComponent';
import { DepthOrder, Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsUtils } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { ChunkSpriteData } from '../systems/ChunkSystem';
import { BaseEntity } from './BaseEntity';
import { SpriteEntity } from './SpriteEntity';

export const defaultWindmillParams = {
    label: 'Windmill',
    x: 0,
    y: 0,
    r: 0,
    sx: 1,
    sy: 1,
    props: {
        speed: 1,
        z: 0,
    },
};

export type WindmillParams = typeof defaultWindmillParams;

export class WindmillBladesEntity extends BaseEntity
{
    public sprite: Sprite;
    public params!: WindmillParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;

    private upAngle: string;
    private rightAngle: string;
    private leftAngle: string;
    private downAngle: string;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);
        this.physics.harmPlayer = true;

        this.sprite = Sprite.from(Shared.assets.getTexture('windmill-blades'));
        this.sprite.anchor.set(0.5);
        this.view.addChild(this.sprite);
        this.physics.physicsFixedUpdateRunner.add(this as any);

        this.upAngle = '0';
        this.rightAngle = Math.fround(Math.PI / 4).toFixed(1);
        this.downAngle = Math.fround(Math.PI / 2).toFixed(1);
        this.leftAngle = Math.fround(3 * Math.PI / 4).toFixed(1);
    }

    public levelReset(): void
    {
        this.physics.mainBody.angle = 0;
    }

    protected physicsFixedUpdate(): void
    {
        const angle = Math.fround(this.physics.angle % Math.PI).toFixed(1);

        if (
            angle === this.upAngle
            || angle === this.rightAngle
            || angle === this.downAngle
            || angle === this.leftAngle
        )
        {
            const approxDist = Math.abs(this.view.x);

            if (approxDist < 1500)
            {
                const volume = 1 - (approxDist / 1500);

                throttle(this.playSfx, 100, this)(volume);
            }
        }
    }

    public build(params: Partial<WindmillParams> = {}): void
    {
        this.physics.clearParts();
        this.params = { ...defaultWindmillParams, ...params };
        const absx = Math.abs(this.params.sx);
        // const absy = Math.abs(this.params.sy);

        const body = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y,
            angularVelocity: (this.params.props.speed || 1),
            fixedX: true,
            fixedY: true,
            type: p2.Body.KINEMATIC,
        },
        {
            type: p2.Shape.BOX,
            width: 25 * absx,
            height: 440 * absx,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
        },
        {
            type: p2.Shape.BOX,
            width: 440 * absx,
            height: 25 * absx,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
        },
        {
            type: p2.Shape.CIRCLE,
            radius: 47 * absx,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
        });

        this.physics.addParts(body);

        this.view.scale.set(absx);
        this.rotation = this.params.r;
        // this.z = this.params.props.z || DepthOrder.OBSTACLE;
        this.z = DepthOrder.OBSTACLE;

        // This is messing with z-order of blades and base, to investigate
        this.culling.updateBounds();
    }

    private playSfx(volume: number)
    {
        this.game?.soundSystem.playSfx('windmill', volume);
    }
}

export class WindmillFactory
{
    public static match(data: ChunkSpriteData): boolean
    {
        return data.img === 'windmill';
    }

    public static create(game: Game, params: WindmillParams): void
    {
        const base = game.pool.get(SpriteEntity) as SpriteEntity;

        base.build({
            ...params,
            // z: params.props.z || DepthOrder.OBSTACLE,
            z: DepthOrder.OBSTACLE - 0.00000000001,
            ax: 0.5,
            ay: 0.19,
            sx: Math.abs(params.sx),
            sy: Math.abs(params.sx),
            texture: 'windmill-base',
        });
        game.level.addEntity(base);

        const blades = game.pool.get(WindmillBladesEntity) as WindmillBladesEntity;

        blades.build(params);
        game.level.addEntity(blades);
    }
}
